import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * `useFollowScrollLeft(sourceRef, targetRef)` — keep `targetRef`'s `scrollLeft`
 * equal to `sourceRef`'s, ONE WAY (source → target). Added 2026-09-23 for the
 * Crew Gantt's sticky date band (owner ruling, option C): the date axis moved
 * OUT of the x-only timeline into a band pinned at the top of the vertical
 * scroller, so it no longer scrolls sideways with the bars on its own; its
 * `overflow-hidden` clip pane follows the timeline instead.
 *
 * 🔑 ONE-WAY BY DESIGN. The target is a CLIP (`overflow-hidden`), never a
 * scroller a user can move — the timeline is the one horizontal truth. A
 * two-way sync would need a re-entrancy latch and is the shape that desyncs.
 * Horizontal input that lands on the band (a `deltaX` wheel, a drag) is
 * FORWARDED to the source by the caller, never applied to the target.
 *
 * 🔑 THE SOURCE ELEMENT IS TRACKED IN STATE, for `useDragScroll`'s reason: a
 * `ref.current` is not reactive, so a listener attached in an effect keyed on
 * the ref never sees an element that mounts after an early return.
 *
 * 🔑 IT ALSO RE-ALIGNS AFTER EVERY RENDER (layout effect, no deps), because a
 * re-render can change the target's content (zoom → new axis width) without
 * the source firing a `scroll` event — and a stale target reads as dates that
 * do not line up with their bars. The write is skipped when already equal.
 *
 * jsdom has no layout: a jsdom test proves the WRITE, never that the two panes
 * line up on screen — that is a browser measurement.
 */
export default function useFollowScrollLeft(sourceRef, targetRef) {
  const [source, setSource] = useState(null);
  useEffect(() => {
    setSource((current) => (sourceRef.current === current ? current : sourceRef.current));
  });

  useLayoutEffect(() => {
    const s = sourceRef.current;
    const t = targetRef.current;
    if (s && t && t.scrollLeft !== s.scrollLeft) t.scrollLeft = s.scrollLeft;
  });

  useEffect(() => {
    if (!source) return undefined;
    const follow = () => {
      const t = targetRef.current;
      if (t && t.scrollLeft !== source.scrollLeft) t.scrollLeft = source.scrollLeft;
    };
    source.addEventListener('scroll', follow, { passive: true });
    follow();
    return () => source.removeEventListener('scroll', follow);
  }, [source, targetRef]);
}
